import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, MapPin, Calendar, DollarSign, Users, Loader2, Trophy, FileDown, Target, Clock } from 'lucide-react';
import { programsAPI, getImageUrl } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const categoryMap = {
  es: { Technology: 'Tecnologia', Healthcare: 'Salud', Sustainability: 'Sustentabilidad', Education: 'Educacion', Finance: 'Finanzas' }
};
const translateCategory = (cat, lang) => (lang === 'es' && categoryMap.es[cat]) ? categoryMap.es[cat] : cat;

const ProgramDetail = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const t = translations[language].programDetail;
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProgram = async () => {
    try {
      setLoading(true);
      const data = await programsAPI.getById(id);
      setProgram(data);
    } catch (error) {
      console.error('Error fetching program:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-black" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-black mb-4">
            {language === 'es' ? 'Desafío no encontrado' : 'Challenge not found'}
          </h2>
          <Button asChild>
            <Link to="/programs">{t.backToPrograms}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const daysRemaining = Math.ceil(
    (new Date(program.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-gradient-to-b from-sand-50 to-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/programs">
              <ArrowLeft className="mr-2 w-4 h-4" /> {t.backToPrograms}
            </Link>
          </Button>
        </div>
      </section>

      {/* Hero Image */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="rounded-lg overflow-hidden h-96">
          <img
            src={getImageUrl(program.image)}
            alt={program.name}
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 min-w-0 overflow-hidden">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Badge
                  className={`${
                    program.status === 'open'
                      ? 'bg-black text-white'
                      : 'bg-gray-300 text-gray-700'
                  }`}
                >
                  {program.status === 'open' ? t.applicationsOpen : t.applicationsClosed}
                </Badge>
                <Badge variant="outline" className="border-gray-300">
                  {translateCategory(program.category, language)}
                </Badge>
              </div>
              <h1 className="text-4xl font-bold text-black mb-4">{program.name}</h1>
              <p className="text-xl text-gray-600 mb-2">
                {language === 'es' ? 'por' : 'by'} {program.organization}
              </p>
            </div>

            <div className="prose max-w-none">
              <h2 className="text-2xl font-bold text-black mb-4">{t.aboutProgram}</h2>
              <p className="text-gray-700 leading-relaxed mb-6">{program.description}</p>

              {/* Challenge section */}
              {program.challenge && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-6 h-6" style={{ color: '#6949b6' }} />
                    <h2 className="text-2xl font-bold text-black">{language === 'es' ? 'El Desafio' : 'The Challenge'}</h2>
                  </div>
                  <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-lg mb-6">
                    <div className="text-gray-700 leading-relaxed prose prose-sm max-w-none break-words overflow-hidden" dangerouslySetInnerHTML={{ __html: program.challenge }} />
                  </div>
                </>
              )}

              {/* Prizes section */}
              {program.prizes && program.prizes.length > 0 && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-6 h-6" style={{ color: '#ff9400' }} />
                    <h2 className="text-2xl font-bold text-black">{language === 'es' ? 'Premios' : 'Prizes'}</h2>
                  </div>
                  <div className="space-y-3 mb-6">
                    {program.prizes.map((prize, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 rounded-lg border-2 border-gray-100 hover:border-orange-200 transition-colors">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: index === 0 ? '#ff9400' : index === 1 ? '#6949b6' : '#64748b' }}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900">{prize.place}</span>
                            <span className="text-xl font-bold" style={{ color: '#ff9400' }}>${prize.amount}</span>
                          </div>
                          {prize.description && (
                            <p className="text-sm text-gray-600">{prize.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Application Process - only show if custom steps defined */}
              {program.applicationSteps && program.applicationSteps.length > 0 && (
                <>
                  <h2 className="text-2xl font-bold text-black mb-4">{t.applicationProcess}</h2>
                  <div className="space-y-4 mb-6">
                    {program.applicationSteps.map((step, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="font-semibold text-black">{step.title}</h3>
                          <p className="text-gray-600 text-sm">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Eligibility - only show if custom requirements defined */}
              {program.eligibilityRequirements && program.eligibilityRequirements.length > 0 && (
                <>
                  <h2 className="text-2xl font-bold text-black mb-4">{t.eligibility}</h2>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 mb-6">
                    {program.eligibilityRequirements.map((requirement, index) => (
                      <li key={index}>{requirement}</li>
                    ))}
                  </ul>
                </>
              )}

              {/* Custom Sections */}
              {program.customSections && program.customSections.length > 0 && (
                program.customSections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="mb-6">
                    <h2 className="text-2xl font-bold text-black mb-4">{section.title}</h2>
                    {section.items && section.items.length > 0 && (
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex}>{item.text}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
            <Card className="border-2 border-black">
              <CardContent className="p-6 space-y-6">
                {/* Start Date */}
                {program.startDate && (
                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <Calendar className="w-5 h-5" />
                      <span className="text-sm font-medium">{language === 'es' ? 'Fecha de Inicio' : 'Start Date'}</span>
                    </div>
                    <p className="text-lg font-semibold text-black">
                      {new Date(program.startDate).toLocaleDateString(language === 'es' ? 'es-CL' : 'en-US', {
                        month: 'long', day: 'numeric', year: 'numeric'
                      })}
                    </p>
                  </div>
                )}

                <div className={program.startDate ? "border-t border-gray-200 pt-6" : ""}>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <Calendar className="w-5 h-5" />
                    <span className="text-sm font-medium">{t.applicationDeadline}</span>
                  </div>
                  <p className="text-lg font-semibold text-black">
                    {new Date(program.deadline).toLocaleDateString(language === 'es' ? 'es-CL' : 'en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <MapPin className="w-5 h-5" />
                    <span className="text-sm font-medium">{t.location}</span>
                  </div>
                  <p className="text-lg font-semibold text-black">{program.location}</p>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <Users className="w-5 h-5" />
                    <span className="text-sm font-medium">{t.totalApplicants}</span>
                  </div>
                  <p className="text-lg font-semibold text-black">{program.applicants}</p>
                </div>

                {/* Target Stages */}
                {program.targetStages && program.targetStages.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <span className="text-sm font-medium">{language === 'es' ? 'Etapas de Startups' : 'Startup Stages'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {program.targetStages.map((stage) => (
                        <span key={stage} className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {stage}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Verticals */}
                {program.verticals && program.verticals.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <span className="text-sm font-medium">{language === 'es' ? 'Verticales' : 'Verticals'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {program.verticals.map((v) => (
                        <span key={v} className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Technical Bases Download */}
                {program.technicalBasesUrl && (
                  <div className="border-t border-gray-200 pt-6">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${program.technicalBasesUrl}${program.technicalBasesFilename ? '?filename=' + encodeURIComponent(program.technicalBasesFilename) : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={program.technicalBasesFilename || true}
                      className="flex items-center gap-2 w-full px-4 py-3 rounded-lg border-2 border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors text-sm font-medium"
                      data-testid="download-technical-bases"
                    >
                      <FileDown className="w-5 h-5" />
                      {language === 'es' ? 'Descargar Bases Tecnicas' : 'Download Technical Bases'}
                    </a>
                    {program.technicalBasesFilename && (
                      <p className="text-xs text-gray-500 mt-1 text-center">{program.technicalBasesFilename}</p>
                    )}
                  </div>
                )}

                <div className="border-t border-gray-200 pt-6">
                  <Button
                    asChild
                    size="lg"
                    className="w-full bg-black text-white hover:bg-gray-800"
                    disabled={program.status !== 'open'}
                  >
                    <Link to={`/apply?program=${program._id}`}>
                      {program.status === 'open' ? t.applyNow : t.applicationsClosed}
                    </Link>
                  </Button>
                  {program.status === 'open' && daysRemaining > 0 && (
                    <div className={`mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold ${
                      daysRemaining <= 7 
                        ? 'bg-red-100 text-red-800 border border-red-200' 
                        : daysRemaining <= 30 
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : 'bg-blue-100 text-blue-800 border border-blue-200'
                    }`}>
                      <Clock className="w-4 h-4" />
                      {t.closesIn} <span className="font-bold text-lg leading-none">{daysRemaining}</span> {t.days}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProgramDetail;
